import React, { useState } from 'react';
import { UserDefinedTemplate } from '../../features/templates/types';
import { FiSearch } from 'react-icons/fi';
import { updateTemplateStatus } from '../../features/templates/templateActions';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../app/store';
import fetchUserDefinedTemplates from '../../features/templates/userDefinedTemplatesApi';
import { toast } from 'react-toastify';
import TemplateReviewModal from './TemplateReviewModal';

const FiSearchComponent = FiSearch as any;

interface Props {
  templates: UserDefinedTemplate[];
  onSelect?: (id: string) => void;
  // Pagination props
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

const NewTemplates: React.FC<Props> = ({
  templates,
  onSelect,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<UserDefinedTemplate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const activeTemplates = templates.filter((t) => t.status === 'DRAFT');

  const categories = Array.from(
    new Set(activeTemplates.map((t) => t.category).filter(Boolean))
  );

  const handleCategoryChange = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const filteredTemplates = activeTemplates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (template.description || '')
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategories.length === 0 ||
      selectedCategories.includes(template.category);

    return matchesSearch && matchesCategory;
  });

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    const apiPage = page - 1;
    onPageChange(apiPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openActionModal = (template: UserDefinedTemplate) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleStatusUpdate = async (status: 'APPROVED' | 'REJECTED') => {
    if (!selectedTemplate) return;

    setIsUpdating(true);
    try {
      await updateTemplateStatus(selectedTemplate.id, status, selectedTemplate);
      toast.success(`Template ${status.toLowerCase()} successfully!`);
      closeModal();
      // Refresh the current page after status update
      dispatch(fetchUserDefinedTemplates({
        orgId: 'org_123456',
        page: currentPage,
        size: pageSize,
      }));
    } catch (err) {
      toast.error('Failed to update template status');
    } finally {
      setIsUpdating(false);
    }
  };

  // Calculate which items to show
  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount);

  return (
    <div className='flex gap-6'>
      {/* Sidebar */}
      <div
        className='w-1/5 bg-white rounded-md p-5 border border-gray-200 shadow-sm sticky top-6'
        style={{ height: 'fit-content' }}
      >
        <button
          className='w-full text-left font-medium mb-4 px-3 py-2 rounded flex justify-between items-center'
          style={{ backgroundColor: '#FFF4E5', color: '#FF8C00' }}
        >
          New Templates
          <span className='bg-orange-600 text-white text-xs px-2 py-1 rounded'>
            {totalCount}
          </span>
        </button>
        <div className='space-y-2'>
          {categories.map((cat) => {
            const count = activeTemplates.filter(
              (t) => t.category === cat
            ).length;
            if (count === 0) return null;
            return (
              <label
                key={cat}
                className='flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer'
              >
                <input
                  type='checkbox'
                  checked={selectedCategories.includes(cat)}
                  onChange={() => handleCategoryChange(cat)}
                  className='w-4 h-4 text-orange-600 rounded focus:ring-orange-500'
                />
                <span className='text-sm text-gray-700 flex-1'>{cat}</span>
                <span className='text-xs text-gray-500'>({count})</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1'>
        <div className='flex items-center justify-between mb-6'>
          <div className='relative flex-1 w-full'>
            <input
              type='text'
              placeholder='Search new templates...'
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              className='w-full pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500'
            />
            <FiSearchComponent
              className='absolute right-3 top-3 text-gray-400'
              size={20}
            />
          </div>
        </div>

        {filteredTemplates.length > 0 ? (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className='group relative bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300'
                >
                  <div className='aspect-[4/3] bg-gray-100 relative overflow-hidden'>
                    <img
                      src={
                        template.thumbnail ||
                        'https://placehold.co/400x300?text=Template'
                      }
                      alt={template.name}
                      className='w-full h-full object-cover transition-transform group-hover:scale-105'
                      onError={(e: any) =>
                      (e.currentTarget.src =
                        'https://placehold.co/400x300?text=No+Preview')
                      }
                    />

                    {/* Hover: Action Button for Admin */}
                    <div
                      className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer'
                      onClick={(e) => {
                        e.stopPropagation();
                        openActionModal(template);
                      }}
                    >
                      <button className='px-6 py-3 bg-white text-gray-800 font-medium rounded-lg hover:bg-gray-100 transition shadow-lg'>
                        Action
                      </button>
                    </div>
                  </div>

                  <div className='p-4'>
                    <div className='flex justify-between items-start mb-2'>
                      <h3 className='font-semibold text-gray-800 truncate pr-2'>
                        {template.name}
                      </h3>
                      <span className='bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full'>
                        DRAFT
                      </span>
                    </div>
                    <p className='text-sm text-gray-600 line-clamp-2'>
                      {template.description || 'No description'}
                    </p>
                    <p className='text-xs text-gray-500 mt-2'>
                      {template.category || 'Uncategorized'}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* MODERN PAGINATION CONTROLS */}
            {totalPages > 1 && (
              <div className='mt-10 flex items-center justify-center gap-2'>
                <button
                  onClick={() => handlePageChange(currentPage)}
                  disabled={currentPage === 0}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Previous
                </button>

                {(() => {
                  // Calculate page numbers to show
                  const pages = [];
                  const total = totalPages;
                  const current = currentPage + 1;
                  const delta = 1; // Number of pages to show on each side of current

                  // Always show first page
                  pages.push(1);

                  // Calculate range
                  let rangeStart = Math.max(2, current - delta);
                  let rangeEnd = Math.min(total - 1, current + delta);

                  // Add ellipsis after first page if needed
                  if (rangeStart > 2) {
                    pages.push('...');
                  }

                  // Add pages in range
                  for (let i = rangeStart; i <= rangeEnd; i++) {
                    pages.push(i);
                  }

                  // Add ellipsis before last page if needed
                  if (rangeEnd < total - 1) {
                    pages.push('...');
                  }

                  // Always show last page if more than 1 page
                  if (total > 1) {
                    pages.push(total);
                  }

                  return pages.map((page, index) => {
                    if (page === '...') {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className='px-3 py-2 text-sm text-gray-500'
                        >
                          …
                        </span>
                      );
                    }

                    const pageNumber = page as number;
                    const isActive = pageNumber === current;

                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                            ? 'bg-orange-500 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  });
                })()}

                <button
                  onClick={() => handlePageChange(currentPage + 2)}
                  disabled={currentPage + 1 === totalPages}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Next
                </button>
              </div>
            )}

            <div className='mt-4 text-center text-sm text-gray-600'>
              Showing {startItem} to {endItem} of {totalCount} templates
            </div>
          </>
        ) : (
          <div className='text-center py-16'>
            <p className='text-gray-500 text-lg'>No new templates found</p>
            <p className='text-gray-400 mt-2'>
              {searchQuery || selectedCategories.length > 0
                ? 'Try adjusting your search or filters'
                : 'No draft templates awaiting review.'}
            </p>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {selectedTemplate && (
        <TemplateReviewModal
          template={selectedTemplate}
          isOpen={isModalOpen}
          onClose={closeModal}
          onApprove={() => handleStatusUpdate('APPROVED')}
          onReject={() => handleStatusUpdate('REJECTED')}
          isUpdating={isUpdating}
        />
      )}
    </div>
  );
};

export default NewTemplates;